import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import {Palette, Sparkles, Wine, Calendar, ArrowRight, Check, Send, X} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { getCachedText, preloadSiteContent } from '@/hooks/useSiteContent'
import EditableText from '@/components/EditableText'

type Univers = 'peintures' | 'sculptures' | 'terrariums' | 'photographie' | 'autre'
type Duree = 10 | 20 | 30

const ARTISTES_ICONS = [Sparkles, Palette, Sparkles]

const ESPACES_FALLBACK = [
  {
    titre: 'Espace galerie',
    texte: "Un espace lumineux dédié à la mise en valeur des œuvres, aux accueils sur rendez-vous et aux présentations privées en petit comité."
  },
  {
    titre: 'Pièce esthétique confidentielle',
    texte: "Une pièce dédiée aux prestations esthétiques et corporelles, organisée de manière professionnelle, calme et discrète."
  },
  {
    titre: 'Zone technique discrète',
    texte: "Une zone réservée au rangement du matériel professionnel, sans accueil de clientèle, afin de maintenir un environnement ordonné et fluide."
  }
]

const ARTISTES_FALLBACK = [
  {
    nom: 'La fée sorcière',
    discipline: 'Terrariums vivants',
    description: "Créations organiques et évolutives, où la nature devient œuvre."
  },
  {
    nom: 'Alexandra Goris',
    discipline: 'Peintures',
    description: "Travail pictural sensible, où couleur et matière ouvrent des espaces intérieurs."
  },
  {
    nom: 'Marie-Aude',
    discipline: 'Sculptures',
    description: "Formes incarnées, matière vivante, intuition rendue visible."
  }
]

const CYCLES_FALLBACK = [
  { key: 'cycle_1', label: 'Cycle court', duree: '10 jours', prix: '400 CHF' },
  { key: 'cycle_2', label: 'Cycle médian', duree: '20 jours', prix: '800 CHF' },
  { key: 'cycle_3', label: 'Cycle complet', duree: '30 jours', prix: '1200 CHF' }
]

const CYCLE_BULLETS_FALLBACK = [
  "Accueil de 2 à 3 artistes maximum simultanément.",
  "Possibilité d'organiser une présentation privée pendant la période d'exposition.",
  "Événements strictement encadrés sur deux créneaux : 18h00 – 19h30 ou 19h00 – 20h30.",
  "Aucun dépassement. Flux maîtrisé, ambiance calme, absence de nuisance."
]

const ACTIVITES_FALLBACK = [
  'Prestations esthétiques haut de gamme — onglerie, soins corporels spécialisés.',
  'Épilation et soins avec machine professionnelle.',
  'Galerie artistique avec expositions par cycles de 10 à 30 jours.',
  "Événements privés encadrés — visites, présentations d'artistes, apéritifs en petit comité."
]

export default function Sanctuaire() {
  const [showArtisteForm, setShowArtisteForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [, setTick] = useState(0)
  const [form, setForm] = useState({
    artisteNom: '',
    artisteEmail: '',
    artisteTelephone: '',
    univers: 'peintures' as Univers,
    description: '',
    siteWeb: '',
    duree: 10 as Duree,
    presentationPrivee: false,
    creneauPresentation: 'aucun' as '18h00-19h30' | '19h00-20h30' | 'aucun'
  })

  useEffect(() => {
    preloadSiteContent()
    const handler = () => setTick((n) => n + 1)
    window.addEventListener('site-content-updated', handler)
    return () => window.removeEventListener('site-content-updated', handler)
  }, [])

  const t = (key: string, fallback: string) => getCachedText('sanctuaire', key, fallback)

  const montant = form.duree === 10 ? 400 : form.duree === 20 ? 800 : 1200

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const now = new Date().toISOString()
      await lumi.entities.expositions.create({
        artisteNom: form.artisteNom,
        artisteEmail: form.artisteEmail,
        artisteTelephone: form.artisteTelephone,
        univers: form.univers,
        description: form.description,
        siteWeb: form.siteWeb,
        duree: form.duree,
        montant,
        statut: 'candidature',
        conventionSignee: false,
        paiementRecu: false,
        presentationPrivee: form.presentationPrivee,
        creneauPresentation: form.creneauPresentation,
        creator: form.artisteEmail || 'public',
        createdAt: now,
        updatedAt: now
      })
      setSubmitted(true)
      setTimeout(() => {
        setShowArtisteForm(false)
        setSubmitted(false)
        setForm({
          artisteNom: '',
          artisteEmail: '',
          artisteTelephone: '',
          univers: 'peintures',
          description: '',
          siteWeb: '',
          duree: 10,
          presentationPrivee: false,
          creneauPresentation: 'aucun'
        })
      }, 3500)
    } catch (err: any) {
      console.error(err)
      setError(err?.message || "Échec de l'envoi de la candidature.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fade-in">
      {/* Hero — Manifeste */}
      <section className="min-h-[80vh] flex flex-col items-center justify-center px-6 py-24 text-center">
        <div className="max-w-4xl">
          <EditableText pageKey="sanctuaire" sectionKey="hero_eyebrow" fallback="— Le Sanctuaire —" as="p" contentType="subtitle" className="text-xs tracking-[0.4em] uppercase text-[#c9a96e] mb-12 shimmer" />

          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.15] text-[#f5f1ea] mb-10">
            <EditableText pageKey="sanctuaire" sectionKey="hero_title_1" fallback="L'art qui transforme" as="span" contentType="title" /><br />
            <EditableText pageKey="sanctuaire" sectionKey="hero_title_2" fallback="de l'intérieur." as="span" contentType="title" className="italic text-[#c9a96e]" />
          </h1>

          <div className="w-px h-16 bg-gradient-to-b from-[#c9a96e]/60 to-transparent mx-auto mb-10" />

          <EditableText
            pageKey="sanctuaire"
            sectionKey="hero_manifeste"
            fallback={"Entrer.\nRessentir.\nSe transformer."}
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif text-xl md:text-2xl text-[#f5f1ea]/85 leading-loose italic max-w-2xl mx-auto mb-8 whitespace-pre-line"
          />

          <EditableText
            pageKey="sanctuaire"
            sectionKey="hero_desc"
            fallback={"Un lieu signature. Discret, structuré, esthétique, artistique et maîtrisé.\nUne activité exclusivement sur rendez-vous."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base md:text-lg text-[#8a8580] leading-loose max-w-xl mx-auto font-light whitespace-pre-line"
          />
        </div>
      </section>

      {/* Positionnement */}
      <section className="px-6 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <EditableText pageKey="sanctuaire" sectionKey="position_eyebrow" fallback="Positionnement" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]" />
          <EditableText
            pageKey="sanctuaire"
            sectionKey="position_quote"
            fallback="Un espace réel, structuré, dédié à l'accueil d'expériences artistiques et esthétiques."
            as="p"
            contentType="paragraph"
            multiline
            className="font-serif text-2xl md:text-3xl text-[#f5f1ea]/90 leading-loose italic"
          />
          <EditableText
            pageKey="sanctuaire"
            sectionKey="position_text"
            fallback={"Un lieu où l'exigence, la qualité et la maîtrise permettent une expérience sensible et ancrée.\nL'esthétique, l'art et le bien-être se complètent — sans nuisance, sans passage libre."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base text-[#8a8580] leading-loose font-light max-w-2xl mx-auto whitespace-pre-line"
          />
        </div>
      </section>

      {/* Trois espaces */}
      <section className="px-6 lg:px-12 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <EditableText pageKey="sanctuaire" sectionKey="espaces_eyebrow" fallback="Organisation des espaces" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-6" />
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea]">
              <EditableText pageKey="sanctuaire" sectionKey="espaces_title_1" fallback="Trois" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="espaces_title_2" fallback="territoires" as="span" contentType="title" className="italic text-[#c9a96e]" />
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {ESPACES_FALLBACK.map((espace, i) => (
              <div
                key={i}
                className="relative px-8 py-12 border border-[#c9a96e]/20 hover:border-[#c9a96e]/50 transition-all duration-700"
              >
                <p className="text-xs tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-6">
                  0{i + 1}
                </p>
                <h3 className="font-serif text-2xl text-[#f5f1ea] mb-6">
                  <EditableText pageKey="sanctuaire" sectionKey={`espace_${i + 1}_titre`} fallback={espace.titre} as="span" contentType="title" />
                </h3>
                <div className="w-8 h-px bg-[#c9a96e]/40 mb-6" />
                <p className="text-sm text-[#a8a39e] font-light leading-relaxed">
                  <EditableText pageKey="sanctuaire" sectionKey={`espace_${i + 1}_texte`} fallback={espace.texte} as="span" contentType="paragraph" multiline />
                </p>
              </div>
            ))}
          </div>

          <EditableText
            pageKey="sanctuaire"
            sectionKey="espaces_note"
            fallback="Une séparation claire entre les espaces de vie privée et les activités sur rendez-vous."
            as="p"
            contentType="paragraph"
            className="text-center text-sm text-[#a8a39e] font-light italic mt-12 max-w-2xl mx-auto leading-relaxed"
          />
        </div>
      </section>

      {/* Galerie — artistes pressentis */}
      <section className="px-6 lg:px-12 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <EditableText pageKey="sanctuaire" sectionKey="galerie_eyebrow" fallback="Galerie du Laboratoire" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-6" />
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea] mb-6">
              <EditableText pageKey="sanctuaire" sectionKey="galerie_title_1" fallback="L'art devient" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="galerie_title_2" fallback="expérience" as="span" contentType="title" className="italic text-[#c9a96e]" />
            </h2>
            <EditableText
              pageKey="sanctuaire"
              sectionKey="galerie_desc"
              fallback={"Des œuvres choisies pour leur capacité à toucher, éveiller et créer une résonance intérieure.\nChaque exposition est pensée comme une immersion — un moment suspendu entre matière, regard et ressenti."}
              as="p"
              contentType="paragraph"
              multiline
              className="text-base text-[#a8a39e] leading-loose font-light max-w-2xl mx-auto whitespace-pre-line"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {ARTISTES_FALLBACK.map((artiste, i) => {
              const Icon = ARTISTES_ICONS[i]
              return (
                <div
                  key={i}
                  className="group relative px-8 py-12 border border-[#c9a96e]/15 hover:border-[#c9a96e]/40 transition-all duration-700 bg-gradient-to-b from-transparent to-[#c9a96e]/[0.02]"
                >
                  <Icon className="w-6 h-6 text-[#c9a96e]/70 mb-6" />
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-3">
                    <EditableText pageKey="sanctuaire" sectionKey={`artiste_${i + 1}_discipline`} fallback={artiste.discipline} as="span" contentType="subtitle" />
                  </p>
                  <h3 className="font-serif text-2xl text-[#f5f1ea] mb-4 group-hover:text-[#c9a96e] transition-colors duration-700">
                    <EditableText pageKey="sanctuaire" sectionKey={`artiste_${i + 1}_nom`} fallback={artiste.nom} as="span" contentType="title" />
                  </h3>
                  <div className="w-8 h-px bg-[#c9a96e]/30 mb-5 group-hover:w-16 transition-all duration-700" />
                  <p className="text-sm text-[#a8a39e] font-light leading-relaxed italic">
                    <EditableText pageKey="sanctuaire" sectionKey={`artiste_${i + 1}_desc`} fallback={artiste.description} as="span" contentType="paragraph" multiline />
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Fonctionnement des expositions */}
      <section className="px-6 lg:px-12 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <EditableText pageKey="sanctuaire" sectionKey="cycles_eyebrow" fallback="Exposer dans la galerie" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-6" />
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea]">
              <EditableText pageKey="sanctuaire" sectionKey="cycles_title_1" fallback="Un cycle, une" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="cycles_title_2" fallback="présence" as="span" contentType="title" className="italic text-[#c9a96e]" />
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {CYCLES_FALLBACK.map((cycle, i) => (
              <div
                key={cycle.key}
                className="text-center px-8 py-12 border border-[#c9a96e]/20 hover:border-[#c9a96e]/50 transition-all duration-500"
              >
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-4">
                  <EditableText pageKey="sanctuaire" sectionKey={`${cycle.key}_label`} fallback={cycle.label} as="span" contentType="subtitle" />
                </p>
                <p className="font-serif text-3xl text-[#f5f1ea] mb-3">
                  <EditableText pageKey="sanctuaire" sectionKey={`${cycle.key}_duree`} fallback={cycle.duree} as="span" contentType="title" />
                </p>
                <div className="w-10 h-px bg-[#c9a96e]/40 mx-auto mb-4" />
                <p className="font-serif text-2xl italic text-[#c9a96e]">
                  <EditableText pageKey="sanctuaire" sectionKey={`${cycle.key}_prix`} fallback={cycle.prix} as="span" contentType="title" />
                </p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto space-y-4 text-[#a8a39e] font-light leading-loose">
            {CYCLE_BULLETS_FALLBACK.map((line, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-2 h-2 rounded-full bg-[#c9a96e]/60 mt-2 flex-shrink-0" />
                <p className="text-sm md:text-base">
                  <EditableText pageKey="sanctuaire" sectionKey={`cycle_bullet_${i + 1}`} fallback={line} as="span" contentType="paragraph" multiline />
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => setShowArtisteForm(true)}
              className="group inline-flex items-center gap-4 px-10 py-4 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#262626] text-xs tracking-[0.3em] uppercase transition-all duration-500"
            >
              <EditableText pageKey="sanctuaire" sectionKey="cycles_cta" fallback="Candidater pour exposer" as="span" contentType="button" />
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Activités proposées */}
      <section className="px-6 lg:px-12 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <EditableText pageKey="sanctuaire" sectionKey="activites_eyebrow" fallback="Activités proposées" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-6" />
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea]">
              <EditableText pageKey="sanctuaire" sectionKey="activites_title_1" fallback="Qualité." as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="activites_title_2" fallback="Discrétion." as="span" contentType="title" className="italic text-[#c9a96e]" /> <EditableText pageKey="sanctuaire" sectionKey="activites_title_3" fallback="Maîtrise." as="span" contentType="title" />
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
            {ACTIVITES_FALLBACK.map((activite, i) => (
              <div key={i} className="flex items-start gap-4">
                <Check className="w-4 h-4 text-[#c9a96e] mt-1 flex-shrink-0" />
                <p className="text-sm md:text-base text-[#8a8580] font-light leading-relaxed">
                  <EditableText pageKey="sanctuaire" sectionKey={`activite_${i + 1}`} fallback={activite} as="span" contentType="paragraph" multiline />
                </p>
              </div>
            ))}
          </div>

          <EditableText
            pageKey="sanctuaire"
            sectionKey="activites_note"
            fallback="Chaque prestation est organisée sur rendez-vous afin de garantir qualité, fluidité et absence de nuisance."
            as="p"
            contentType="paragraph"
            className="text-center text-sm text-[#a8a39e]/80 font-light italic mt-16 max-w-2xl mx-auto leading-loose"
          />
        </div>
      </section>

      {/* Événements privés */}
      <section className="px-6 lg:px-12 py-24 border-t border-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto text-center">
          <Wine className="w-8 h-8 text-[#c9a96e] mx-auto mb-8" />
          <EditableText pageKey="sanctuaire" sectionKey="events_eyebrow" fallback="Événements privés" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-6" />
          <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea] mb-10">
            <EditableText pageKey="sanctuaire" sectionKey="events_title_1" fallback="En" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="events_title_2" fallback="petit comité" as="span" contentType="title" className="italic text-[#c9a96e]" />
          </h2>
          <EditableText
            pageKey="sanctuaire"
            sectionKey="events_desc"
            fallback={"Visites de galerie, présentations d'artistes, apéritifs en petit comité.\nChaque événement est encadré et confidentiel."}
            as="p"
            contentType="paragraph"
            multiline
            className="text-base md:text-lg text-[#a8a39e] leading-loose font-light max-w-2xl mx-auto mb-12 whitespace-pre-line"
          />

          <div className="inline-flex items-center gap-6 px-8 py-5 border border-[#c9a96e]/30">
            <Calendar className="w-5 h-5 text-[#c9a96e]" />
            <div className="text-left">
              <EditableText pageKey="sanctuaire" sectionKey="events_slot_label" fallback="Créneaux fixes" as="p" contentType="subtitle" className="text-[10px] tracking-[0.3em] uppercase text-[#c9a96e]/60 mb-1" />
              <p className="font-serif text-lg text-[#f5f1ea]">
                <EditableText pageKey="sanctuaire" sectionKey="events_slot_1" fallback="18h00 – 19h30" as="span" contentType="paragraph" /> <span className="text-[#8a8580] mx-2">·</span> <EditableText pageKey="sanctuaire" sectionKey="events_slot_2" fallback="19h00 – 20h30" as="span" contentType="paragraph" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs finaux */}
      <section className="px-6 py-32 border-t border-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-5xl text-[#f5f1ea] mb-6">
              <EditableText pageKey="sanctuaire" sectionKey="ctas_title_1" fallback="Entrer en" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="ctas_title_2" fallback="lien" as="span" contentType="title" className="italic text-[#c9a96e]" />
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/contact?intention=rdv"
              className="group block px-10 py-12 border border-[#c9a96e]/20 hover:border-[#c9a96e]/60 transition-all duration-700 text-center"
            >
              <Sparkles className="w-6 h-6 text-[#c9a96e] mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-[#f5f1ea] group-hover:text-[#c9a96e] transition-colors duration-700 mb-4">
                <EditableText pageKey="sanctuaire" sectionKey="cta_1_title" fallback="Prendre rendez-vous" as="span" contentType="title" />
              </h3>
              <p className="text-sm text-[#8a8580] font-light leading-relaxed">
                <EditableText pageKey="sanctuaire" sectionKey="cta_1_desc" fallback="Visite privée, prestation esthétique, présentation d'œuvres." as="span" contentType="paragraph" multiline />
              </p>
            </Link>

            <button
              onClick={() => setShowArtisteForm(true)}
              className="group block px-10 py-12 border border-[#c9a96e]/20 hover:border-[#c9a96e]/60 transition-all duration-700 text-center"
            >
              <Palette className="w-6 h-6 text-[#c9a96e] mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-[#f5f1ea] group-hover:text-[#c9a96e] transition-colors duration-700 mb-4">
                <EditableText pageKey="sanctuaire" sectionKey="cta_2_title" fallback="Exposer dans la galerie" as="span" contentType="title" />
              </h3>
              <p className="text-sm text-[#8a8580] font-light leading-relaxed">
                <EditableText pageKey="sanctuaire" sectionKey="cta_2_desc" fallback="Candidater pour un cycle de 10, 20 ou 30 jours." as="span" contentType="paragraph" multiline />
              </p>
            </button>
          </div>
        </div>
      </section>

      {/* Modal candidature artiste */}
      {showArtisteForm && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 md:p-8">
          <div className="relative bg-[#262626] border border-[#c9a96e]/30 max-w-2xl w-full my-8">
            <button
              onClick={() => !submitting && setShowArtisteForm(false)}
              className="absolute top-4 right-4 text-[#a8a39e] hover:text-[#c9a96e] transition-colors p-2"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 md:p-12">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full border border-[#c9a96e] flex items-center justify-center mx-auto mb-8 breathe">
                    <Check className="w-6 h-6 text-[#c9a96e]" />
                  </div>
                  <EditableText pageKey="sanctuaire" sectionKey="form_success_title" fallback="Candidature reçue." as="p" contentType="title" className="font-serif text-2xl text-[#c9a96e] italic mb-4" />
                  <EditableText
                    pageKey="sanctuaire"
                    sectionKey="form_success_text"
                    fallback={"Une réponse personnelle viendra dans les prochains jours.\nMerci pour ton geste."}
                    as="p"
                    contentType="paragraph"
                    multiline
                    className="text-sm text-[#a8a39e] font-light leading-relaxed whitespace-pre-line"
                  />
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-6">
                  <div className="text-center mb-8">
                    <EditableText pageKey="sanctuaire" sectionKey="form_eyebrow" fallback="Galerie du Laboratoire" as="p" contentType="subtitle" className="text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3" />
                    <h2 className="font-serif text-3xl text-[#f5f1ea]">
                      <EditableText pageKey="sanctuaire" sectionKey="form_title_1" fallback="Candidater pour" as="span" contentType="title" /> <EditableText pageKey="sanctuaire" sectionKey="form_title_2" fallback="exposer" as="span" contentType="title" className="italic text-[#c9a96e]" />
                    </h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                        <EditableText pageKey="sanctuaire" sectionKey="form_label_nom" fallback="Nom *" as="span" contentType="paragraph" />
                      </label>
                      <input
                        type="text"
                        required
                        value={form.artisteNom}
                        onChange={(e) => setForm({ ...form, artisteNom: e.target.value })}
                        className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                        <EditableText pageKey="sanctuaire" sectionKey="form_label_email" fallback="Email *" as="span" contentType="paragraph" />
                      </label>
                      <input
                        type="email"
                        required
                        value={form.artisteEmail}
                        onChange={(e) => setForm({ ...form, artisteEmail: e.target.value })}
                        className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                        <EditableText pageKey="sanctuaire" sectionKey="form_label_telephone" fallback="Téléphone" as="span" contentType="paragraph" />
                      </label>
                      <input
                        type="tel"
                        value={form.artisteTelephone}
                        onChange={(e) => setForm({ ...form, artisteTelephone: e.target.value })}
                        className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                        <EditableText pageKey="sanctuaire" sectionKey="form_label_site" fallback="Site / portfolio" as="span" contentType="paragraph" />
                      </label>
                      <input
                        type="url"
                        placeholder="https://"
                        value={form.siteWeb}
                        onChange={(e) => setForm({ ...form, siteWeb: e.target.value })}
                        className="w-full bg-transparent border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                      <EditableText pageKey="sanctuaire" sectionKey="form_label_univers" fallback="Univers artistique *" as="span" contentType="paragraph" />
                    </label>
                    <select
                      value={form.univers}
                      onChange={(e) => setForm({ ...form, univers: e.target.value as Univers })}
                      className="w-full bg-[#262626] border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors"
                    >
                      <option value="peintures">Peintures</option>
                      <option value="sculptures">Sculptures</option>
                      <option value="terrariums">Terrariums vivants</option>
                      <option value="photographie">Photographie</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-2">
                      <EditableText pageKey="sanctuaire" sectionKey="form_label_description" fallback="Présentation du projet *" as="span" contentType="paragraph" />
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder={t('form_placeholder_description', 'Quelques mots sur tes œuvres, ton intention pour cette exposition…')}
                      className="w-full bg-transparent border border-[#c9a96e]/20 focus:border-[#c9a96e]/60 p-3 text-[#f5f1ea] font-light outline-none transition-colors resize-none text-sm leading-relaxed"
                    />
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.3em] uppercase text-[#c9a96e] mb-3">
                      <EditableText pageKey="sanctuaire" sectionKey="form_label_duree" fallback="Durée souhaitée *" as="span" contentType="paragraph" />
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      {([10, 20, 30] as Duree[]).map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setForm({ ...form, duree: d })}
                          className={`py-4 border text-center transition-all ${
                            form.duree === d
                              ? 'border-[#c9a96e] bg-[#c9a96e]/10 text-[#c9a96e]'
                              : 'border-[#c9a96e]/20 text-[#a8a39e] hover:border-[#c9a96e]/50'
                          }`}
                        >
                          <p className="font-serif text-xl">{d}j</p>
                          <p className="text-[10px] tracking-widest uppercase mt-1">
                            {d === 10 ? '400 CHF' : d === 20 ? '800 CHF' : '1200 CHF'}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border border-[#c9a96e]/15 p-5 space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.presentationPrivee}
                        onChange={(e) => setForm({ ...form, presentationPrivee: e.target.checked })}
                        className="mt-1 accent-[#c9a96e]"
                      />
                      <span className="text-sm text-[#f5f1ea]/80 font-light leading-relaxed">
                        <EditableText pageKey="sanctuaire" sectionKey="form_label_presentation_privee" fallback="Je souhaite organiser une présentation privée pendant la période d'exposition." as="span" contentType="paragraph" multiline />
                      </span>
                    </label>

                    {form.presentationPrivee && (
                      <div className="pl-6">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#c9a96e]/70 mb-2">
                          <EditableText pageKey="sanctuaire" sectionKey="form_label_creneau" fallback="Créneau préféré" as="span" contentType="paragraph" />
                        </label>
                        <select
                          value={form.creneauPresentation}
                          onChange={(e) => setForm({ ...form, creneauPresentation: e.target.value as any })}
                          className="w-full bg-[#262626] border-b border-[#c9a96e]/30 focus:border-[#c9a96e] py-2 text-[#f5f1ea] font-light outline-none transition-colors text-sm"
                        >
                          <option value="aucun">À définir ensemble</option>
                          <option value="18h00-19h30">18h00 – 19h30</option>
                          <option value="19h00-20h30">19h00 – 20h30</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {error && (
                    <p className="text-sm text-red-400 font-light text-center">{error}</p>
                  )}

                  <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <button
                      type="button"
                      onClick={() => setShowArtisteForm(false)}
                      disabled={submitting}
                      className="text-xs tracking-[0.3em] uppercase text-[#a8a39e] hover:text-[#f5f1ea] py-3 px-6 transition-colors"
                    >
                      <EditableText pageKey="sanctuaire" sectionKey="form_btn_annuler" fallback="Annuler" as="span" contentType="button" />
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-3 px-10 py-3 border border-[#c9a96e] text-[#c9a96e] hover:bg-[#c9a96e] hover:text-[#262626] text-xs tracking-[0.3em] uppercase transition-all duration-500 disabled:opacity-60"
                    >
                      <span>{submitting ? t('form_btn_envoi', 'Envoi…') : t('form_btn_envoyer', 'Envoyer la candidature')}</span>
                      {!submitting && <Send className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />}
                    </button>
                  </div>

                  <EditableText
                    pageKey="sanctuaire"
                    sectionKey="form_note"
                    fallback="Convention de réservation envoyée après validation. Paiement à confirmation."
                    as="p"
                    contentType="paragraph"
                    className="text-center text-xs text-[#a8a39e]/70 font-light italic pt-2"
                  />
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
